import { sanitizeRichHtml } from "../../shared/safeHtml";
import { exportEmailMatches, maskExportEmails } from "../../shared/ticket-export";
import { splitQuotedMessage, splitRichMessage } from "../components/MessageBody";
import type { SessionUser } from "./types";
import { isValidTimezone } from "./format";

export function canExportTicketConversation(user: SessionUser | null): boolean {
  return user?.kind === "staff" && Boolean(user.roles?.some(role => role === "admin" || role === "manager"))
    && !user.mfaSetupRequired && !user.passwordChangeRequired
    && (user.permissions.includes("ticket.view") || user.permissions.includes("*"));
}

export function exportTimezone(timeZone?: string): string {
  return isValidTimezone(timeZone) ? timeZone : "UTC";
}

export function formatExportDate(value: string, timeZone = "UTC"): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date not recorded";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit",
    hourCycle: "h23", timeZone: exportTimezone(timeZone), timeZoneName: "shortOffset",
  }).format(date);
}

const quoteSelector = ".gmail_quote, .gmail_extra, blockquote[type='cite'], #divRplyFwdMsg";
const blockTags = new Set(["DIV", "P", "LI", "UL", "OL", "TABLE", "TR", "TD", "TH", "PRE", "BLOCKQUOTE", "H1", "H2", "H3", "H4", "H5", "H6", "BR", "HR"]);
const structuralAttributes = new Set(["colspan", "rowspan", "span", "scope", "start", "reversed", "value", "dir"]);

function removeRemainingQuotes(root: HTMLElement) {
  for (const quote of root.querySelectorAll(quoteSelector)) {
    if (quote.id.toLowerCase() === "divrplyfwdmsg") {
      // Workers also declares a global Element type; this node is browser DOM.
      let node: ChildNode | null = quote as unknown as ChildNode;
      while (node) { const next: ChildNode | null = node.nextSibling; node.remove(); node = next; }
    } else quote.remove();
  }
}

/** Mask rendered text rather than raw HTML, including entity-encoded addresses
 * and addresses split over inline tags. Block boundaries prevent swallowing
 * unrelated text in the next paragraph/cell. No original link URLs survive. */
function maskDomEmails(root: HTMLElement) {
  const nodes: Array<{ node: Text; start: number; end: number }> = [];
  let text = "";
  const visit = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const value = (node.nodeValue || "").replace(/[\u200b-\u200d\u2060\ufeff]/g, "");
      node.nodeValue = value;
      const start = text.length;
      text += value;
      nodes.push({ node: node as Text, start, end: text.length });
      return;
    }
    const block = node.nodeType === Node.ELEMENT_NODE && blockTags.has((node as Element).tagName);
    if (block) text += "\n";
    for (const child of node.childNodes) visit(child);
    if (block) text += "\n";
  };
  visit(root);
  const ranges = exportEmailMatches(text)
    .map(match => ({ start: match.index!, end: match.index! + match[0].length }));
  // Work backwards so original offsets stay valid when one node has several emails.
  for (const range of ranges.reverse()) {
    for (const item of nodes) {
      if (item.end <= range.start || item.start >= range.end) continue;
      const start = Math.max(0, range.start - item.start);
      const end = Math.min(item.end - item.start, range.end - item.start);
      const replacement = range.start >= item.start ? "[email hidden]" : "";
      item.node.data = item.node.data.slice(0, start) + replacement + item.node.data.slice(end);
    }
  }
}

export function prepareExportMessage(message: { text: string; html?: string | null }): string {
  const plain = splitQuotedMessage(message.text);
  // Do not let even a detached preview load remote images or attachment URLs.
  const html = sanitizeRichHtml(message.html || undefined)?.replace(/<img\b[^>]*>/gi, "");
  const rich = html ? splitRichMessage(html) : null;
  const useRich = Boolean(rich?.currentHtml || rich?.quotedHtml) && (!plain.quoted || Boolean(rich?.quotedHtml));
  const doc = new DOMParser().parseFromString("", "text/html");
  const root = doc.body;
  if (useRich && rich?.currentHtml) root.innerHTML = rich.currentHtml;
  else if (!useRich) {
    const paragraph = doc.createElement("p");
    paragraph.textContent = plain.quoted && plain.current === "Reply quoted below" ? "" : plain.current;
    root.appendChild(paragraph);
  }
  removeRemainingQuotes(root);
  for (const element of root.querySelectorAll("*")) {
    const signature = element.classList.contains("agent-signature");
    for (const attribute of [...element.attributes]) {
      if (!structuralAttributes.has(attribute.name)) element.removeAttribute(attribute.name);
    }
    if (signature) element.className = "agent-signature";
  }
  maskDomEmails(root);
  if (!root.textContent?.trim()) return "<p><em>No text to display (quoted content and files omitted).</em></p>";
  return root.innerHTML;
}

export const exportText = maskExportEmails;
